import React, { useState, useEffect } from 'react';
import { getFirestore, doc, onSnapshot, collection, query, where, getDocs, Timestamp } from 'firebase/firestore';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import './Statistics.css';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { getSubscribedUsersCount } from '../../services/oneSignalStatsService';

const ONESIGNAL_API_KEY = process.env.REACT_APP_ONESIGNAL_API_KEY;
const ONESIGNAL_APP_ID = process.env.REACT_APP_ONESIGNAL_APP_ID;

const Statistics = () => {
  const [subscribers, setSubscribers] = useState(0);
  const [installations, setInstallations] = useState(0);
  const [monthlyVisits, setMonthlyVisits] = useState(0);
  const [peakDailyVisits, setPeakDailyVisits] = useState(0);
  const [isAnimating, setIsAnimating] = useState({
    subscribers: false,
    installations: false,
    visits: false,
    peak: false
  });
  const [selectedShop, setSelectedShop] = useState('all');
  const [shops, setShops] = useState([]);
  const [dailyStats, setDailyStats] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [viewMode, setViewMode] = useState('daily');
  const [selectedMonth, setSelectedMonth] = useState(format(new Date(), 'yyyy-MM'));
  const [usersWithoutNotifications, setUsersWithoutNotifications] = useState(0);
  const [totalInstallations, setTotalInstallations] = useState(0);
  const [usersWithNotifications, setUsersWithNotifications] = useState(0);
  const [usersWithoutOneSignalId, setUsersWithoutOneSignalId] = useState(0);
  const [usersWithNullOneSignalId, setUsersWithNullOneSignalId] = useState(0);
  const [inactiveUsers, setInactiveUsers] = useState(0);
  const [inactiveDate, setInactiveDate] = useState('');
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [isMonthPickerOpen, setIsMonthPickerOpen] = useState(false);

  // Tableau des mois en français
  const MOIS_FR = [
    "Janvier", "Février", "Mars", "Avril", "Mai", "Juin",
    "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"
  ];

  useEffect(() => {
    const db = getFirestore();
    
    // Écouter pushStats pour les abonnés et installations
    const unsubscribePush = onSnapshot(doc(db, 'analytics', 'pushStats'), async (doc) => {
      if (doc.exists()) {
        const data = doc.data();
        const newSubscribers = data.totalSubscribed || 0;
        const newInstallations = Object.keys(data.devices || {}).length;
        
        // Compter les appareils avec notificationsEnabled: false depuis pushStats
        const disabledNotifications = Object.values(data.devices || {}).filter(enabled => enabled === false).length;

        try {
          // Récupérer les statistiques depuis OneSignal
          const oneSignalStats = await getSubscribedUsersCount();
          
          setSubscribers(newSubscribers);
          setInstallations(newInstallations);
          
          // Utiliser disabledNotifications de pushStats comme source fiable locale
          setUsersWithoutNotifications(disabledNotifications);
          
          // Utiliser uniquement les appareils vraiment inactifs de OneSignal (invalid_identifier: true)
          setUsersWithoutOneSignalId(oneSignalStats.invalidIdentifiers);
          
          // Utiliser les appareils actifs avec notifications activées de OneSignal
          setUsersWithNotifications(oneSignalStats.subscribed);
          
          // Supprimer tous les logs de débogage
          /* 
          // console.log("Statistiques OneSignal complètes:", oneSignalStats);
          // console.log(`Total installations: ${totalInstallations}`);
          // console.log(`Utilisateurs avec notifications: ${oneSignalStats.subscribed}`);
          // console.log(`Utilisateurs avec notifications désactivées (pushStats): ${disabledNotifications}`);
          // console.log(`Utilisateurs avec notifications désactivées (OneSignal): ${oneSignalStats.notificationsDisabled}`);
          // console.log(`Appareils inactifs: ${oneSignalStats.invalidIdentifiers}`);
          // console.log(`Total des sous-catégories: ${oneSignalStats.subscribed + disabledNotifications + oneSignalStats.invalidIdentifiers}`);
          */
        } catch (error) {
          // Ne pas supprimer ce log d'erreur
          console.error('Erreur lors de la récupération des statistiques OneSignal:', error);
        }
      }
    });

    // Écouter visits pour les visites
    const unsubscribeVisits = onSnapshot(doc(db, 'analytics', 'visits'), (doc) => {
      if (doc.exists()) {
        const data = doc.data();
        
        // Visites mensuelles
        const currentMonth = new Date().toISOString().slice(0, 7);
        const currentMonthVisits = data.monthlyVisits?.[currentMonth] || 0;
        setMonthlyVisits(currentMonthVisits);

        // Pic journalier
        const dailyVisits = data.dailyVisits || {};
        const maxVisits = Math.max(...Object.values(dailyVisits));
        setPeakDailyVisits(maxVisits);
      }
    });

    // Animer toutes les cartes
    setIsAnimating({
      subscribers: true,
      installations: true,
      visits: true,
      peak: true
    });

    // Reset des animations après 1s
    setTimeout(() => {
      setIsAnimating({
        subscribers: false,
        installations: false,
        visits: false,
        peak: false
      });
    }, 1000);

    // Récupérer la liste des boutiques depuis shopInstall
    const unsubscribeShops = onSnapshot(collection(db, 'shopInstall'), (snapshot) => {
      const shopsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setShops(shopsData);
    });

    // Observer les stats de la boutique sélectionnée
    const unsubscribeShopStats = selectedShop && selectedShop !== 'all' ? 
      onSnapshot(doc(db, 'shopInstall', selectedShop), (doc) => {
        if (doc.exists()) {
          const data = doc.data();
          const dailyData = data.dailyStats || {};
          
          // Convertir les données en format pour le graphique
          const formattedData = Object.entries(dailyData).map(([date, stats]) => ({
            date,
            iOS: stats.iOS || 0,
            Android: stats.Android || 0,
            total: stats.total || 0
          })).sort((a, b) => a.date.localeCompare(b.date));

          setDailyStats(formattedData);
        }
      }) : null;

    // Calculer le total des installations depuis shopInstall
    const unsubscribeShopInstall = onSnapshot(collection(db, 'shopInstall'), (snapshot) => {
      let total = 0;
      snapshot.forEach(doc => {
        const data = doc.data();
        total += data.total || 0;
      });
      setTotalInstallations(total);
    });

    // Récupérer le nombre d'utilisateurs avec oneSignalId sur null
    const fetchUsersWithNullOneSignalId = async () => {
      try {
        const usersRef = collection(db, 'users');
        const q = query(usersRef, where('oneSignalId', '==', null));
        const querySnapshot = await getDocs(q);
        setUsersWithNullOneSignalId(querySnapshot.size);
      } catch (error) {
        // Ne pas supprimer ce log d'erreur
        console.error('Erreur lors de la récupération des utilisateurs avec oneSignalId null:', error);
      }
    };

    // Récupérer le nombre d'utilisateurs inactifs (utilisation < 30 jours après installation)
    const fetchInactiveUsers = async () => {
      try {
        // Récupérer tous les utilisateurs qui ont à la fois firstInstallDate et lastVisitDate
        const usersRef = collection(db, 'users');
        
        // D'abord récupérer tous les utilisateurs pour vérifier les données
        const allUsersQuery = query(usersRef);
        const allUsersSnapshot = await getDocs(allUsersQuery);
        
        // Filtrer manuellement ceux qui ont les deux dates
        const allUsers = [];
        allUsersSnapshot.forEach(doc => {
          allUsers.push({
            id: doc.id,
            ...doc.data()
          });
        });
        
        const usersWithDates = allUsers.filter(user => 
          user.firstInstallDate && user.lastVisitDate
        );
        
        let inactiveCount = 0;
        
        // Calculer pour chaque utilisateur la différence entre première installation et dernière visite
        usersWithDates.forEach(userData => {
          try {
            // Vérifier le type et effectuer la conversion en Date si nécessaire
            const firstInstallDate = userData.firstInstallDate.toDate ? 
              userData.firstInstallDate.toDate() : new Date(userData.firstInstallDate);
            
            const lastVisitDate = userData.lastVisitDate.toDate ? 
              userData.lastVisitDate.toDate() : new Date(userData.lastVisitDate);
            
            // Calculer la différence en jours
            const diffTime = Math.abs(lastVisitDate - firstInstallDate);
            const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
            
            // Si l'utilisateur a été actif moins de 30 jours après son installation
            // ET n'a pas été actif depuis plus de 30 jours
            const now = new Date();
            const daysSinceLastVisit = Math.ceil(
              Math.abs(now - lastVisitDate) / (1000 * 60 * 60 * 24)
            );
            
            if (diffDays < 30 && daysSinceLastVisit > 30) {
              inactiveCount++;
            }
          } catch (error) {
            // Ne pas supprimer ce log d'erreur
            console.error(`Erreur lors du traitement de l'utilisateur ${userData.id}:`, error);
          }
        });
        
        setInactiveUsers(inactiveCount);
      } catch (error) {
        // Ne pas supprimer ce log d'erreur
        console.error('Erreur lors de la récupération des utilisateurs inactifs:', error);
      }
    };

    fetchUsersWithNullOneSignalId();
    fetchInactiveUsers();

    return () => {
      unsubscribePush();
      unsubscribeVisits();
      unsubscribeShops();
      if (unsubscribeShopStats) unsubscribeShopStats();
      unsubscribeShopInstall();
    };
  }, [selectedShop]);

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="custom-tooltip">
          <p className="tooltip-date">{new Date(label).toLocaleDateString('fr-FR')}</p>
          <div className="tooltip-stats">
            <p className="tooltip-value">iOS: {payload[0].value}</p>
            <p className="tooltip-value">Android: {payload[1].value}</p>
            <p className="tooltip-value">Total: {payload[2].value}</p>
          </div>
        </div>
      );
    }
    return null;
  };

  // Fonction pour formater la date selon le mode
  const formatDate = (date) => {
    return viewMode === 'daily' 
      ? date.toISOString().split('T')[0]
      : format(date, 'yyyy-MM');
  };

  const formatFirestoreDate = (date) => {
    if (viewMode === 'daily') {
      return format(date, 'yyyy-MM-dd');  // Utilise format de date-fns pour garder le fuseau horaire local
    } else {
      return format(date, 'yyyy-MM');  // Format mensuel
    }
  };

  // Fonction modifiée pour calculer les stats globales
  const getStats = () => {
    if (selectedShop === 'all') {
      // Calcul pour toutes les boutiques
      if (viewMode === 'daily') {
        const formattedDate = formatFirestoreDate(selectedDate);
        return shops.reduce((totals, shop) => {
          const shopStats = shop.dailyStats?.[formattedDate] || { iOS: 0, Android: 0, total: 0 };
          return {
            iOS: totals.iOS + (shopStats.iOS || 0),
            Android: totals.Android + (shopStats.Android || 0),
            total: totals.total + (shopStats.total || 0)
          };
        }, { iOS: 0, Android: 0, total: 0 });
      } else {
        // Stats mensuelles pour toutes les boutiques
        return shops.reduce((totals, shop) => {
          const shopStats = shop.monthlyStats?.[selectedMonth] || { iOS: 0, Android: 0, total: 0 };
          return {
            iOS: totals.iOS + (shopStats.iOS || 0),
            Android: totals.Android + (shopStats.Android || 0),
            total: totals.total + (shopStats.total || 0)
          };
        }, { iOS: 0, Android: 0, total: 0 });
      }
    } else {
      // Stats pour une boutique spécifique (code existant)
      if (viewMode === 'daily') {
        const formattedDate = formatFirestoreDate(selectedDate);
        return dailyStats.find(stat => stat.date === formattedDate) || {
          iOS: 0,
          Android: 0,
          total: 0
        };
      } else {
        const data = shops.find(shop => shop.id === selectedShop)?.monthlyStats?.[selectedMonth] || {
          iOS: 0,
          Android: 0,
          total: 0
        };
        return data;
      }
    }
  };

  // Function to handle date selection
  const handleDateChange = (date) => {
    setSelectedDate(date);
    setIsDatePickerOpen(false);
  };

  // Function to handle month selection
  const handleMonthChange = (date) => {
    setSelectedMonth(format(date, 'yyyy-MM'));
    setIsDatePickerOpen(false);
  };

  // Remplacer les fonctions existantes par une seule fonction de gestion
  const handleDatePickerToggle = () => {
    setIsDatePickerOpen(!isDatePickerOpen);
  };

  const CalendarModal = ({ isOpen, onClose, viewMode, onSelectDate, onSelectMonth, selectedDate, selectedMonth }) => {
    const [currentDate, setCurrentDate] = useState(new Date());
    const [currentMonth, setCurrentMonth] = useState(new Date());

    const months = [
      'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
      'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
    ];

    const weekDays = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];

    const getDaysInMonth = (date) => {
      const year = date.getFullYear();
      const month = date.getMonth();
      const daysInMonth = new Date(year, month + 1, 0).getDate();
      const firstDayOfMonth = new Date(year, month, 1).getDay();
      const days = [];

      // Ajuster pour commencer par Lundi (1) au lieu de Dimanche (0)
      const adjustedFirstDay = firstDayOfMonth === 0 ? 6 : firstDayOfMonth - 1;

      // Jours du mois précédent
      const prevMonth = new Date(year, month, 0);
      const daysInPrevMonth = prevMonth.getDate();
      for (let i = adjustedFirstDay - 1; i >= 0; i--) {
        days.push({
          date: new Date(year, month - 1, daysInPrevMonth - i),
          isCurrentMonth: false
        });
      }

      // Jours du mois actuel
      for (let i = 1; i <= daysInMonth; i++) {
        days.push({
          date: new Date(year, month, i),
          isCurrentMonth: true
        });
      }

      // Jours du mois suivant
      const remainingDays = 42 - days.length; // 6 semaines complètes
      for (let i = 1; i <= remainingDays; i++) {
        days.push({
          date: new Date(year, month + 1, i),
          isCurrentMonth: false
        });
      }

      return days;
    };

    const handlePrevMonth = () => {
      if (viewMode === 'daily') {
        setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
      } else {
        setCurrentMonth(new Date(currentMonth.getFullYear() - 1, currentMonth.getMonth(), 1));
      }
    };

    const handleNextMonth = () => {
      if (viewMode === 'daily') {
        setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
      } else {
        setCurrentMonth(new Date(currentMonth.getFullYear() + 1, currentMonth.getMonth(), 1));
      }
    };

    const isDateDisabled = (date) => {
      const today = new Date();
      return date > today;
    };

    const isMonthDisabled = (year, month) => {
      const today = new Date();
      return year > today.getFullYear() || 
             (year === today.getFullYear() && month > today.getMonth());
    };

    if (!isOpen) return null;

    return (
      <div className="calendar-modal-overlay" onClick={onClose}>
        <div className="calendar-modal" onClick={e => e.stopPropagation()}>
          <div className="calendar-modal-header">
            <h2 className="calendar-modal-title">
              {viewMode === 'daily' ? 'Sélectionner une date' : 'Sélectionner un mois'}
            </h2>
            <button className="calendar-modal-close" onClick={onClose}>
              <i className="fas fa-times"></i>
            </button>
          </div>

          <div className="calendar-modal-content">
            {viewMode === 'daily' ? (
              <>
                <div className="calendar-navigation">
                  <div className="calendar-nav-month">
                    {months[currentDate.getMonth()]} {currentDate.getFullYear()}
                  </div>
                  <div className="calendar-nav-buttons">
                    <button className="calendar-nav-btn" onClick={handlePrevMonth}>
                      <i className="fas fa-chevron-left"></i>
                    </button>
                    <button className="calendar-nav-btn" onClick={handleNextMonth}>
                      <i className="fas fa-chevron-right"></i>
                    </button>
                  </div>
                </div>

                <div className="calendar-grid">
                  {weekDays.map(day => (
                    <div key={day} className="calendar-weekday">{day}</div>
                  ))}
                  
                  {getDaysInMonth(currentDate).map((day, index) => {
                    const isSelected = selectedDate && 
                      day.date.toDateString() === selectedDate.toDateString();
                    const isToday = day.date.toDateString() === new Date().toDateString();
                    const isDisabled = isDateDisabled(day.date);

                    return (
                      <div
                        key={index}
                        className={`calendar-day ${!day.isCurrentMonth ? 'calendar-day-disabled' : ''} 
                          ${isSelected ? 'calendar-day-selected' : ''} 
                          ${isToday ? 'calendar-day-today' : ''} 
                          ${isDisabled ? 'calendar-day-disabled' : ''}`}
                        onClick={() => !isDisabled && day.isCurrentMonth && onSelectDate(day.date)}
                      >
                        {day.date.getDate()}
                      </div>
                    );
                  })}
                </div>
              </>
            ) : (
              <div className="month-grid">
                {months.map((month, index) => {
                  const isSelected = selectedMonth && 
                    new Date(selectedMonth).getMonth() === index &&
                    new Date(selectedMonth).getFullYear() === currentMonth.getFullYear();
                  const isDisabled = isMonthDisabled(currentMonth.getFullYear(), index);

                  return (
                    <div
                      key={month}
                      className={`month-item 
                        ${isSelected ? 'month-item-selected' : ''} 
                        ${isDisabled ? 'month-item-disabled' : ''}`}
                      onClick={() => !isDisabled && onSelectMonth(new Date(currentMonth.getFullYear(), index, 1))}
                    >
                      {month}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Composant DatePickerModal pour la vue jour
  const DayPickerModal = ({ isOpen, onClose }) => {
    if (!isOpen || viewMode !== 'daily') return null;

    return (
      <div className="custom-datepicker-overlay" onClick={onClose}>
        <div className="custom-datepicker-modal day-picker-modal" onClick={e => e.stopPropagation()}>
          <div className="custom-datepicker-header">
            <h3>Sélectionner une date</h3>
            <button className="custom-datepicker-close" onClick={onClose}>
              <i className="fas fa-times"></i>
            </button>
          </div>
          <div className="custom-datepicker-container">
            <DatePicker
              selected={selectedDate}
              onChange={handleDateChange}
              inline
              locale={fr}
              maxDate={new Date()}
              minDate={new Date(2020, 0, 1)}
              showMonthDropdown
              showYearDropdown
              dropdownMode="select"
              calendarClassName="custom-calendar day-calendar"
              renderCustomHeader={({
                date,
                changeYear,
                changeMonth,
                decreaseMonth,
                increaseMonth,
                prevMonthButtonDisabled,
                nextMonthButtonDisabled
              }) => (
                <div className="custom-header">
                  <button
                    className="custom-nav-button"
                    onClick={decreaseMonth}
                    disabled={prevMonthButtonDisabled}
                  >
                    <i className="fas fa-chevron-left"></i>
                  </button>
                  <div className="custom-selects">
                    <select
                      value={date.getFullYear()}
                      onChange={({ target: { value } }) => changeYear(value)}
                      className="custom-year-select"
                    >
                      {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i).map(
                        year => (
                          <option key={year} value={year}>
                            {year}
                          </option>
                        )
                      )}
                    </select>
                    <select
                      value={date.getMonth()}
                      onChange={({ target: { value } }) => changeMonth(value)}
                      className="custom-month-select"
                    >
                      {MOIS_FR.map((month, i) => (
                        <option key={month} value={i}>
                          {month}
                        </option>
                      ))}
                    </select>
                  </div>
                  <button
                    className="custom-nav-button"
                    onClick={increaseMonth}
                    disabled={nextMonthButtonDisabled}
                  >
                    <i className="fas fa-chevron-right"></i>
                  </button>
                </div>
              )}
            />
          </div>
        </div>
      </div>
    );
  };

  // Composant MonthPickerModal pour la vue mois
  const MonthPickerModal = ({ isOpen, onClose }) => {
    if (!isOpen || viewMode !== 'monthly') return null;

    return (
      <div className="custom-datepicker-overlay" onClick={onClose}>
        <div className="custom-datepicker-modal month-picker-modal" onClick={e => e.stopPropagation()}>
          <div className="custom-datepicker-header">
            <h3>Sélectionner un mois</h3>
            <button className="custom-datepicker-close" onClick={onClose}>
              <i className="fas fa-times"></i>
            </button>
          </div>
          <div className="custom-datepicker-container">
            <DatePicker
              selected={new Date(selectedMonth + '-01')}
              onChange={handleMonthChange}
              inline
              showMonthYearPicker
              dateFormat="MM/yyyy"
              locale={fr}
              maxDate={new Date()}
              minDate={new Date(2020, 0, 1)}
              calendarClassName="custom-calendar month-calendar"
            />
          </div>
        </div>
      </div>
    );
  };

  // Fonction pour obtenir les statistiques d'une période spécifique
  const getShopStats = (shop, type) => {
    switch (type) {
      case 'daily':
        const currentDate = formatFirestoreDate(selectedDate);
        return shop.dailyStats?.[currentDate] || { total: 0, iOS: 0, Android: 0 };
      case 'monthly':
        return shop.monthlyStats?.[selectedMonth] || { total: 0, iOS: 0, Android: 0 };
      case 'total':
        return {
          total: shop.total || 0,
          iOS: shop.iOS || 0,
          Android: shop.Android || 0
        };
      default:
        return { total: 0, iOS: 0, Android: 0 };
    }
  };

  // Fonction pour formater la date d'affichage
  const formatDisplayDate = (date) => {
    return format(date, 'dd MMMM yyyy', { locale: fr });
  };

  return (
    <div className="statistics-container">
      <h1>Statistiques</h1>
      <div className="user-analysis-section">
        <div className="user-analysis-grid">
          <div className="user-analysis-card total">
            <div className="user-analysis-icon">
              <i className="fas fa-users"></i>
            </div>
            <div className="user-analysis-content">
              <h3>Total des Installations</h3>
              <div className="user-analysis-value">{totalInstallations}</div>
              <div className="user-analysis-detail">
                <span>Depuis le début de l'application</span>
              </div>
            </div>
          </div>

          <div className="user-analysis-card active">
            <div className="user-analysis-icon">
              <i className="fas fa-bell"></i>
            </div>
            <div className="user-analysis-content">
              <h3>Utilisateurs avec Notifications</h3>
              <div className="user-analysis-value">{usersWithNotifications}</div>
              <div className="user-analysis-detail">
                <span className="positive">✓ Notifications activées</span>
              </div>
            </div>
          </div>

          <div className="user-analysis-card inactive">
            <div className="user-analysis-icon">
              <i className="fas fa-ban"></i>
            </div>
            <div className="user-analysis-content">
              <h3>Utilisateurs sans Notifications</h3>
              <div className="user-analysis-value">{usersWithoutNotifications}</div>
              <div className="user-analysis-detail">
                <span className="negative">✗ Notifications désactivées</span>
              </div>
            </div>
          </div>

          <div className="user-analysis-card inactive-users">
            <div className="user-analysis-icon">
              <i className="fas fa-user-clock"></i>
            </div>
            <div className="user-analysis-content">
              <h3>Utilisateurs Inactifs</h3>
              <div className="user-analysis-value">{inactiveUsers}</div>
              <div className="user-analysis-detail">
                <span className="negative">⏱️ Moins de 30 jours d'utilisation après installation</span>
              </div>
            </div>
          </div>

          <div className="user-analysis-card visits">
            <div className="user-analysis-icon">
              <i className="fas fa-chart-line"></i>
            </div>
            <div className="user-analysis-content">
              <h3>Visites ce mois-ci</h3>
              <div className="user-analysis-value">{monthlyVisits}</div>
              <div className="user-analysis-detail">
                <span className="info">📈 Visites du mois en cours</span>
              </div>
            </div>
          </div>

          <div className="user-analysis-card peak">
            <div className="user-analysis-icon">
              <i className="fas fa-trophy"></i>
            </div>
            <div className="user-analysis-content">
              <h3>Record de visites</h3>
              <div className="user-analysis-value">{peakDailyVisits}</div>
              <div className="user-analysis-detail">
                <span className="info">🏆 Plus grand nombre de visites en une journée</span>
              </div>
            </div>
          </div>

          <div className="user-analysis-card activation-rate">
            <div className="user-analysis-icon">
              <i className="fas fa-chart-pie"></i>
            </div>
            <div className="user-analysis-content">
              <h3>Taux d'Activation</h3>
              <div className="user-analysis-value">
                {totalInstallations > 0 ? 
                  ((usersWithNotifications / totalInstallations) * 100).toFixed(1) : 0}%
              </div>
              <div className="user-analysis-detail">
                <span className="info">📊 Taux de notifications activées</span>
              </div>
            </div>
          </div>

          <div className="user-analysis-card declined">
            <div className="user-analysis-icon">
              <i className="fas fa-times-circle"></i>
            </div>
            <div className="user-analysis-content">
              <h3>Refus de Notifications</h3>
              <div className="user-analysis-value">
                {Math.max(0, (totalInstallations - (usersWithNotifications + usersWithoutNotifications) - inactiveUsers))}
              </div>
              <div className="user-analysis-detail">
                <span className="negative">✗ Notifications refusées à l'installation</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="shop-statistics-container">
        <div className="shop-data-header">
          <h2>Statistiques boutiques</h2>
          <div className="shop-controls">
            <select 
              value={selectedShop}
              onChange={(e) => setSelectedShop(e.target.value)}
              className="shop-select"
            >
              <option value="all">Toutes les boutiques</option>
              {shops.map(shop => (
                <option key={shop.id} value={shop.id}>
                  {shop.name}
                </option>
              ))}
            </select>

            <div className="view-selector">
              <button 
                className={`view-btn ${viewMode === 'daily' ? 'active' : ''}`}
                onClick={() => {
                  setViewMode('daily');
                  setIsDatePickerOpen(false);
                }}
              >
                <i className="fas fa-calendar-day"></i>
                <span>Jour</span>
              </button>
              <button 
                className={`view-btn ${viewMode === 'monthly' ? 'active' : ''}`}
                onClick={() => {
                  setViewMode('monthly');
                  setIsDatePickerOpen(false);
                }}
              >
                <i className="fas fa-calendar-alt"></i>
                <span>Mois</span>
              </button>
            </div>

            <button 
              className="date-picker-button"
              onClick={handleDatePickerToggle}
            >
              <i className="fas fa-calendar"></i>
              <span>
                {viewMode === 'daily' 
                  ? format(selectedDate, 'dd MMMM yyyy', { locale: fr })
                  : format(new Date(selectedMonth + '-01'), 'MMMM yyyy', { locale: fr })}
              </span>
            </button>
          </div>
        </div>

        {selectedShop && (
          <div className="shop-stats-grid">
            <div className="shop-stat-card">
              <div className="shop-stat-icon">
                <i className="fab fa-apple"></i>
              </div>
              <div className="shop-stat-content">
                <h3>iOS</h3>
                <div className="shop-stat-value">
                  {getStats().iOS}
                </div>
              </div>
            </div>

            <div className="shop-stat-card">
              <div className="shop-stat-icon">
                <i className="fab fa-android"></i>
              </div>
              <div className="shop-stat-content">
                <h3>Android</h3>
                <div className="shop-stat-value">
                  {getStats().Android}
                </div>
              </div>
            </div>

            <div className="shop-stat-card">
              <div className="shop-stat-icon">
                <i className="fas fa-mobile-alt"></i>
              </div>
              <div className="shop-stat-content">
                <h3>Total</h3>
                <div className="shop-stat-value">
                  {getStats().total}
                </div>
              </div>
            </div>
          </div>
        )}
        {isDatePickerOpen && (
          <CalendarModal
            isOpen={isDatePickerOpen}
            onClose={() => setIsDatePickerOpen(false)}
            viewMode={viewMode}
            onSelectDate={handleDateChange}
            onSelectMonth={handleMonthChange}
            selectedDate={selectedDate}
            selectedMonth={selectedMonth}
          />
        )}
      </div>

      <div className="shops-list-container">
        <h2>Liste des boutiques</h2>
        <div className="shops-list-header-info">
          <div className="date-info">
            <div className="info-item">
              <i className="fas fa-calendar-day"></i>
              <span>Jour sélectionné : {formatDisplayDate(selectedDate)}</span>
            </div>
            <div className="info-item">
              <i className="fas fa-calendar-alt"></i>
              <span>Mois sélectionné : {format(new Date(selectedMonth + '-01'), 'MMMM yyyy', { locale: fr })}</span>
            </div>
          </div>
        </div>
        <div className="shops-list">
          {shops.map(shop => {
            const dailyStats = getShopStats(shop, 'daily');
            const monthlyStats = getShopStats(shop, 'monthly');
            const totalStats = getShopStats(shop, 'total');
            
            return (
              <div key={shop.id} className="shop-list-item">
                <div className="shop-list-header">
                  <h3>{shop.name}</h3>
                  <div className="shop-list-badge">{totalStats.total} installations</div>
                </div>
                
                <div className="shop-list-stats">
                  <div className="stat-group">
                    <div className="stat-label">
                      <i className="fas fa-calendar-day"></i>
                      <span>{formatDisplayDate(selectedDate)}</span>
                    </div>
                    <div className="stat-values">
                      <div className="stat-value">
                        <i className="fab fa-apple"></i>
                        <span>{dailyStats.iOS}</span>
                      </div>
                      <div className="stat-value">
                        <i className="fab fa-android"></i>
                        <span>{dailyStats.Android}</span>
                      </div>
                      <div className="stat-value total">
                        <i className="fas fa-mobile-alt"></i>
                        <span>{dailyStats.total}</span>
                      </div>
                    </div>
                  </div>

                  <div className="stat-group">
                    <div className="stat-label">
                      <i className="fas fa-calendar-alt"></i>
                      <span>{format(new Date(selectedMonth + '-01'), 'MMMM yyyy', { locale: fr })}</span>
                    </div>
                    <div className="stat-values">
                      <div className="stat-value">
                        <i className="fab fa-apple"></i>
                        <span>{monthlyStats.iOS}</span>
                      </div>
                      <div className="stat-value">
                        <i className="fab fa-android"></i>
                        <span>{monthlyStats.Android}</span>
                      </div>
                      <div className="stat-value total">
                        <i className="fas fa-mobile-alt"></i>
                        <span>{monthlyStats.total}</span>
                      </div>
                    </div>
                  </div>

                  <div className="stat-group">
                    <div className="stat-label">
                      <i className="fas fa-chart-line"></i>
                      <span>Total global</span>
                    </div>
                    <div className="stat-values">
                      <div className="stat-value">
                        <i className="fab fa-apple"></i>
                        <span>{totalStats.iOS}</span>
                      </div>
                      <div className="stat-value">
                        <i className="fab fa-android"></i>
                        <span>{totalStats.Android}</span>
                      </div>
                      <div className="stat-value total">
                        <i className="fas fa-mobile-alt"></i>
                        <span>{totalStats.total}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default Statistics; 